/**
 * One shared read of GET /webpush/client-config.
 *
 * The endpoint answers with the caller's resolved alert config: the team tone /
 * volume / repeat settings (personal overrides already merged), the desktop
 * channel switch, and the per-event channel toggles. Several consumers need it
 * — the order-alert engine (tone) and the seller review queue (desktop popup)
 * — so the request is made ONCE per session and shared, rather than each hook
 * fetching its own copy.
 *
 * The cache is invalidated by the same `configVersion` the Notifications Center
 * bumps when someone saves settings, so a change reaches every consumer without
 * a reload. Failures resolve to null: a missing config must never silence an
 * alert engine, every consumer falls back to its own defaults.
 */

import { apiGetNotificationClientConfig } from "../api/index";
import { readOrderAlertsConfigVersion } from "./notificationState";

let cachedPromise = null;
let cachedVersion = "";

export function fetchNotificationClientConfig() {
  const currentVersion = readOrderAlertsConfigVersion();
  if (currentVersion !== cachedVersion) {
    cachedPromise = null;
    cachedVersion = currentVersion;
  }
  if (!cachedPromise) {
    cachedPromise = apiGetNotificationClientConfig()
      .then((r) => r.data)
      .catch(() => null);
  }
  return cachedPromise;
}

/** Drop the cache so the next call re-reads the server. */
export function invalidateNotificationClientConfig() {
  cachedPromise = null;
  cachedVersion = readOrderAlertsConfigVersion();
}

/**
 * Is `channel` enabled for `event` in this market?
 *
 * Fails OPEN (true) on a missing config or a missing key: an operator who has
 * never opened the settings page still gets alerted. Only an explicit `false`
 * — the channel switch or the per-event toggle — turns a channel off.
 */
export function isEventChannelEnabled(config, event, channel) {
  if (!config) return true;
  if (config[channel] && config[channel].enabled === false) return false;
  const ev = config.events && config.events[event];
  if (!ev) return true;
  return ev[channel] !== false;
}
